// Moved from `src/hooks/mutation/useMutateApplySubscriptionDiscount.ts` (now a
// delegate) at D2 batch 3, ARCFE-976.
//
// A clean port severance: everything that could not cross is a user-visible
// effect or app composition — intl, the success toast, `useBillingErrorToast`,
// both invalidators, and the caller's `onApplied` callback with the ref that
// keeps it fresh. What crossed is the request and the mutation key.

import { useMutation } from '@tanstack/react-query';
import { type ApplySubscriptionDiscountRequest } from '../../contracts';
import { applySubscriptionDiscount } from '../api/subscription';

export type UseMutateApplySubscriptionDiscountProps = {
  /**
   * Fires where the two invalidators, the success toast and the caller's
   * `onApplied` ran — in that order, which the delegate preserves.
   */
  onDiscountApplied?: () => void;
  /**
   * Fires where the billing-error toast ran. The delegate still decides
   * whether the backend's `detail.msg` is shown: that decision reads
   * `AxiosError`, and naming axios in this package's surface is the thing the
   * structural-transport rule exists to prevent.
   */
  onDiscountError?: (error: unknown) => void;
};

export function useMutateApplySubscriptionDiscount({
  onDiscountApplied,
  onDiscountError,
}: UseMutateApplySubscriptionDiscountProps = {}) {
  return useMutation({
    mutationKey: ['applySubscriptionDiscount'],
    mutationFn: async (body: ApplySubscriptionDiscountRequest) => {
      const { data } = await applySubscriptionDiscount(body);
      return data;
    },
    onSuccess: () => {
      onDiscountApplied?.();
    },
    onError: (error) => {
      onDiscountError?.(error);
    },
  });
}
